import { useCallback, useEffect, useRef, useState } from "react";
import { LogoMark } from "./components/Logo";
import LeftNav, { type Mode } from "./components/LeftNav";
import Sidebar from "./components/Sidebar";
import AnswerView from "./components/AnswerView";
import RecentAnalyses from "./components/RecentAnalyses";
import GSPRWizard from "./components/GSPRWizard";
import GeneratedDocuments from "./components/GeneratedDocuments";
import DocAnalysis from "./components/DocAnalysis";
import DocTranslate from "./components/DocTranslate";
import ChatPage from "./components/ChatPage";
import Dashboard from "./components/Dashboard";
import HelpGlossary from "./components/HelpGlossary";
import Icon from "./components/icons";
import SupportPanel from "./components/SupportPanel";
import SettingsPanel from "./components/SettingsPanel";
import WhatsAppFab from "./components/WhatsAppFab";
import { analyze, type AnalyzeResponse } from "./services/api";
import { useTheme } from "./hooks/useTheme";
import { useLang } from "./hooks/useLang";
import { useAnswerLang } from "./hooks/useAnswerLang";
import { useAuth } from "./auth/AuthContext";
import Login from "./pages/Login";
import PaymentResult from "./components/PaymentResult";
import { VERSION } from "./lib/version";

export default function App() {
  const { token, user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const { t } = useLang();
  const { lang: answerLang } = useAnswerLang();
  const [mode, setMode] = useState<Mode>(() => {
    const saved = localStorage.getItem("mode");
    // "management" Ayarlar'a birleştirildi — eski kayıtlı mode'u settings'e taşı.
    return saved === "management" ? "settings" : (saved as Mode) || "home";
  });
  // Mobil (≤900px) sol menü drawer'ı açık mı.
  const [navOpen, setNavOpen] = useState(false);
  // Mevzuat analizi modunda kullanici sidebar'i gizleyebilsin (daha geniş okuma alani)
  const [sidebarHidden, setSidebarHidden] = useState<boolean>(() => localStorage.getItem("sidebar_hidden") === "1");

  const switchMode = (m: Mode) => {
    // Analiz sayfasindan ayrilirken eldeki cevabi "onceki sorgu" say. Cevap
    // SILINMEZ (yanlislikla sekme degistiren kullanici kaybetmesin) ama geri
    // donuldugunde tazeymis gibi gorunmesin — kullanici bunu soru tekrar
    // sorulmus saniyordu, oysa yeni istek gitmiyor (erisim logu ile dogrulandi).
    if (m !== "analyze") setAnswerStale(true);
    setMode(m);
    localStorage.setItem("mode", m);
    setNavOpen(false); // mobilde menüden seçince drawer kapansın
  };
  // Mobil drawer açıkken: Escape ile kapat + arka plan kaymasını kilitle.
  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setNavOpen(false); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [navOpen]);
  const toggleSidebar = () => {
    const next = !sidebarHidden;
    setSidebarHidden(next);
    localStorage.setItem("sidebar_hidden", next ? "1" : "0");
  };
  const [question, setQuestion] = useState("");
  const questionRef = useRef<HTMLTextAreaElement>(null);
  const [submittedQ, setSubmittedQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState<AnalyzeResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [analysesRefresh, setAnalysesRefresh] = useState(0);
  // Cevap ne zaman geldi + kullanici o zamandan beri sayfadan ayrildi mi.
  const [answeredAt, setAnsweredAt] = useState<number | null>(null);
  const [answerStale, setAnswerStale] = useState(false);

  const run = useCallback(async (q: string) => {
    if (!q.trim()) return;
    setLoading(true);
    setErr(null);
    setRes(null);
    setSubmittedQ(q);
    setAnswerStale(false); // yeni sorgu: artik bayat degil
    try {
      const r = await analyze({ question: q, top_k: 8, answer_lang: answerLang });
      setRes(r);
      setAnsweredAt(Date.now());
      setAnalysesRefresh((n) => n + 1); // backend kaydetti, liste yenilensin
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "bilinmeyen hata";
      setErr(msg);
    } finally {
      setLoading(false);
    }
  }, [answerLang]);

  function onPickSample(q: string) {
    setQuestion(q);
    void run(q);
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    void run(question);
  }

  // Sol panel analiz modunda (örnek sorular) + AI sohbet modunda (geçmiş sohbetler)
  // görünür; kullanıcı gizleyebilir. Diğer modlar full-width.
  const sidebarVisible = (mode === "analyze" || mode === "chat") && !sidebarHidden;
  const appClass = "app" + (sidebarVisible ? "" : " app-nosidebar");
  // Mobil üst barda aktif bölüm başlığı (menü etiketinden, baştaki emoji ayıklanır).
  const sectionTitle = t(`mode_${mode}` as Parameters<typeof t>[0]).replace(/^[^\p{L}\p{N}]+/u, "").trim();

  // Ödeme dönüş sayfası (popup landing) — auth'tan bağımsız render edilir.
  if (window.location.pathname.startsWith("/payment/")) return <PaymentResult />;

  // Giriş zorunlu — token yoksa login ekranı.
  if (!token) return <Login />;

  return (
    <div className="shell">
      {/* Mobil üst bar (yalnız ≤900px görünür) — hamburger + marka */}
      <header className="mobile-appbar">
        <button className="appbar-burger" onClick={() => setNavOpen(true)} aria-label={t("nav_open")}>
          <Icon name="menu" size={20} />
        </button>
        <span className="appbar-brand"><LogoMark className="brand-logo-mark" /> <span className="appbar-title">{sectionTitle}</span></span>
      </header>
      {navOpen && <div className="nav-overlay" onClick={() => setNavOpen(false)} aria-hidden />}
      <LeftNav
        mode={mode}
        onSelect={switchMode}
        version={VERSION}
        theme={theme}
        onToggleTheme={toggle}
        userEmail={user?.email}
        onLogout={() => logout()}
        open={navOpen}
        onClose={() => setNavOpen(false)}
      />
      <div className={appClass}>
      {mode === "chat" ? (
        // AI Sohbet kendi iki-kolon düzenini app-grid içine kurar (geçmiş + sohbet).
        <ChatPage sidebarHidden={sidebarHidden} onToggleSidebar={toggleSidebar} />
      ) : (
      <>
      {sidebarVisible && <Sidebar activeQuestion={submittedQ} onPick={onPickSample} />}

      <main className={"main" + (sidebarVisible ? "" : " main-full")}>
        <div className="main-inner">
          {mode === "settings" ? (
            <SettingsPanel />
          ) : mode === "support" ? (
            <SupportPanel />
          ) : mode === "help" ? (
            <HelpGlossary />
          ) : mode === "home" ? (
            <Dashboard onNavigate={switchMode} />
          ) : mode === "gspr" ? (
            <GSPRWizard onFinalized={() => switchMode("documents")} />
          ) : mode === "documents" ? (
            <GeneratedDocuments />
          ) : mode === "docanalysis" ? (
            <DocAnalysis />
          ) : mode === "translate" ? (
            <DocTranslate />
          ) : (
          <>
          <div className="content-toolbar">
            <button
              className="sidebar-toggle"
              onClick={toggleSidebar}
              title={sidebarHidden ? t("sidebar_show") : t("sidebar_hide")}
              aria-label="sidebar toggle"
            >
              <Icon name={sidebarHidden ? "menu" : "x"} size={15} /> {sidebarHidden ? t("sidebar_show") : t("sidebar_hide")}
            </button>
          </div>

          <RecentAnalyses
            refreshToken={analysesRefresh}
            onReplay={(q) => { setQuestion(q); void run(q); }}
          />

          <form className="search" onSubmit={onSubmit}>
            <textarea
              ref={questionRef}
              className="search-input"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => {
                // Ctrl/Cmd+Enter ile gönder (textarea'da Enter yeni satır bırakır).
                if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && !loading && question.trim()) {
                  e.preventDefault();
                  void run(question);
                }
              }}
              placeholder={t("search_placeholder")}
              rows={3}
            />
            <div className="search-actions">
              <button type="submit" disabled={loading || !question.trim()}>
                {loading ? t("btn_loading") : t("btn_ask")}
              </button>
              <button
                type="button"
                className="btn-clear"
                onClick={() => { setQuestion(""); questionRef.current?.focus(); }}
                disabled={loading || !question.trim()}
              >
                {t("btn_clear")}
              </button>
            </div>
          </form>

          {!res && !loading && !err && (
            <div className="empty">
              <h3>{t("empty_title")}</h3>
              <p>{t("empty_body")}</p>
            </div>
          )}

          {loading && (
            <div className="loading">
              <div className="spinner" />
              <div>
                <strong>{t("loading_title")}</strong>
                <div style={{ fontSize: 12.5, color: "var(--fg-mute)" }}>
                  {t("loading_body")}
                </div>
              </div>
            </div>
          )}

          {err && <div className="error">{t("err_prefix")}: {err}</div>}

          {res && <AnswerView res={res} question={submittedQ} stale={answerStale} answeredAt={answeredAt} />}
          </>
          )}
        </div>
      </main>
      </>
      )}
      </div>
      {!navOpen && <WhatsAppFab />}
    </div>
  );
}
